import React, {useState, useEffect, useCallback, useRef} from 'react';
import styled from 'styled-components';
import {Link} from 'react-router-dom';

import {useApi} from '../../lib/api';
import {useUser} from '../../lib/user';
import SideModal from '../../components/SideModal';
import Card from '../../components/Card';
import Button from '../../components/Button';
import Loading from '../../components/Loading';
import Error from '../../components/Error';
import Pagination from '../../components/Pagination';
import ListItem from '../../components/ListItem';
import {T} from '../../components/I18n';
import AdjustmentForm from './AdjustmentForm';
import {canAdjustInventory} from './access';
import {
  PAGE_SIZE,
  usePagedResource,
  formatQty,
  formatSignedQty,
  withUnit,
  getLotLabel,
  getTypeLabel,
  getStatusLabel,
  getTransactionDate,
  formatDateTime,
  getSubmissionPath,
  AttentionIcon,
  ItemTags,
  Negative,
  PaginationContainer
} from './shared';

//Keeps showing the last item while the modal slides closed, and starts each item from a clean slate (first page of
//history, adjustment form closed, item fetched again) by remounting the detail when the item changes or the modal
//opens again, even on the item it showed last.
function InventoryItemModal({itemId, onClose, onChange}) {
  const [shown, setShown] = useState({itemId, opens: 0});
  const wasOpen = useRef(!!itemId);

  useEffect(() => {
    if (itemId) {
      setShown(prev => ({itemId, opens: wasOpen.current ? prev.opens : prev.opens + 1}));
    }
    wasOpen.current = !!itemId;
  }, [itemId]);

  return (
    <InventoryItemDetail
      key={shown.itemId ? `${shown.itemId}-${shown.opens}` : 'none'}
      itemId={shown.itemId}
      open={!!itemId}
      onClose={onClose}
      onChange={onChange}
    />
  );
}

function InventoryItemDetail({itemId, open, onClose, onChange}) {
  const api = useApi();
  const user = useUser();
  const canAdjust = canAdjustInventory(user);
  const [itemState, setItemState] = useState({loading: !!itemId, error: null, item: null});
  const [itemReloadCount, setItemReloadCount] = useState(0);
  const [adjusting, setAdjusting] = useState(false);

  useEffect(() => {
    if (!itemId) {
      return undefined;
    }

    let cancelled = false;
    setItemState(prevState => ({...prevState, loading: true, error: null}));

    api.getInventoryItem(itemId).then(
      item => !cancelled && setItemState({loading: false, error: null, item}),
      error => {
        if (!cancelled) {
          console.error(error);
          setItemState({loading: false, error, item: null});
        }
      }
    );

    return () => {
      cancelled = true;
    };
  }, [itemId, itemReloadCount]);

  const transactions = usePagedResource(
    page => api.getInventoryItemTransactions(itemId, {page, size: PAGE_SIZE}),
    [itemId],
    !!itemId
  );
  const {setPage: setTransactionPage, reload: reloadTransactions} = transactions;

  const handleAdjusted = useCallback(() => {
    setAdjusting(false);
    setItemReloadCount(count => count + 1);
    setTransactionPage(0);
    reloadTransactions();

    if (onChange) {
      onChange();
    }
  }, [onChange, setTransactionPage, reloadTransactions]);

  const {item, error: itemError} = itemState;
  const notFound = itemError && (itemError.status === 404 || itemError.status === 403);

  let content;

  if (!itemId) {
    content = null;
  }
  else if (itemError) {
    content = (
      <Error halfScreen message={notFound
        ? 'This item could not be found, or you don\'t have access to it.'
        : 'Failed to load this item.'} />
    );
  }
  else if (!item) {
    content = <Loading />;
  }
  else {
    content = (
      <SideModal.Content>
        {adjusting && (
          <AdjustmentForm item={item} onCancel={() => setAdjusting(false)} onSaved={handleAdjusted} />
        )}
        <TransactionHistory item={item} transactions={transactions} />
      </SideModal.Content>
    );
  }

  return (
    <SideModal
      open={open}
      title={item ? <T noTranslate>{item.itemName}</T> : 'Inventory Item'}
      header={item && <ItemSummary item={item} />}
      onClose={onClose}
      options={[
        canAdjust && item && !adjusting && (
          <Button onClick={() => setAdjusting(true)}>Adjust Balance</Button>
        ),
        <Button secondary onClick={onClose}>Close</Button>
      ]}>
      {content}
    </SideModal>
  );
}

function ItemSummary({item}) {
  const balance = withUnit(formatQty(item.qty), item.unit);

  return (
    <Summary>
      <div>{getLotLabel(item.lotCode)}</div>
      <div>Balance: {item.qty < 0 ? <Negative>{balance}</Negative> : balance}</div>
      {item.openExceptions > 0 && (
        <div>Has open exceptions<AttentionIcon title="Has open exceptions" /></div>
      )}
      <ItemTags tags={item.tags} />
    </Summary>
  );
}

function TransactionHistory({item, transactions}) {
  const {loading, error, result, page, setPage} = transactions;
  const records = (result && result.content) || [];

  let body;

  if (error) {
    body = <Error halfScreen message="Failed to load the history for this item." />;
  }
  else if (!result) {
    body = <Loading />;
  }
  else if (records.length < 1) {
    body = <Card.Content>Nothing has been recorded for this item yet.</Card.Content>;
  }
  else {
    body = records.map((transaction, i) => (
      <TransactionRow key={transaction.id} first={i === 0} item={item} transaction={transaction} />
    ));
  }

  return (
    <Card>
      <Card.Title>History</Card.Title>
      {body}
      {result && result.totalElements > PAGE_SIZE && (
        <PaginationContainer>
          <Pagination
            mini
            page={page + 1}
            itemsPerPage={PAGE_SIZE}
            totalItems={result.totalElements}
            disabled={loading}
            onClick={nextPage => setPage(nextPage - 1)}
          />
        </PaginationContainer>
      )}
    </Card>
  );
}

function TransactionRow({item, transaction, first}) {
  const date = getTransactionDate(transaction);
  const unit = transaction.itemUnit || item.unit;
  const applied = transaction.qtyDelta !== null && transaction.qtyDelta !== undefined;
  const hasEnteredQty = transaction.enteredQty !== null && transaction.enteredQty !== undefined;
  const hasBalance = transaction.balanceAfter !== null && transaction.balanceAfter !== undefined;
  const statusLabel = getStatusLabel(transaction.status);
  const submissionPath = getSubmissionPath(transaction);

  let change;
  if (applied) {
    const formatted = withUnit(formatSignedQty(transaction.qtyDelta), unit);
    change = transaction.qtyDelta < 0 ? <Negative>{formatted}</Negative> : formatted;
  }
  else {
    change = hasEnteredQty
      ? <>{withUnit(formatQty(transaction.enteredQty), transaction.enteredUnit)} (not applied)</>
      : 'Not applied';
  }

  //the panel is narrow, so the change sits on the title line and the submission is a plain link, like the form
  //names on the forms list, rather than a button column beside the row
  return (
    <ListItem first={first}>
      <HistoryContent>
        <strong>
          {getTypeLabel(transaction.type)}
          {transaction.reversesId && ' (reversal)'}
          {transaction.reversedAt && ' (reversed)'}
          {': '}
          {change}
          {statusLabel && <AttentionIcon title={statusLabel} />}
        </strong>
        <HistorySubtitle>
          {statusLabel && (
            <div>
              {statusLabel}
              {transaction.message && transaction.type !== 'ADJUST' && <>: <T noTranslate>{transaction.message}</T></>}
            </div>
          )}
          {transaction.message && (transaction.type === 'ADJUST' || !statusLabel) && (
            <div>{transaction.type === 'ADJUST' ? 'Reason: ' : ''}<T noTranslate>{transaction.message}</T></div>
          )}
          {hasBalance && (
            <div>Balance after: {transaction.balanceAfter < 0
              ? <Negative>{withUnit(formatQty(transaction.balanceAfter), unit)}</Negative>
              : withUnit(formatQty(transaction.balanceAfter), unit)}</div>
          )}
          <div>
            {date && formatDateTime(date)}
            {transaction.userName && <> · <T noTranslate>{transaction.userName}</T></>}
          </div>
          {submissionPath && <div><Link to={submissionPath}>View submission</Link></div>}
          {transaction.sourceHidden && <div>From a form you don't have access to</div>}
        </HistorySubtitle>
      </HistoryContent>
    </ListItem>
  );
}

const Summary = styled.div`
  color: #666;
  line-height: 1.6;
`;

//laid out like a row on the forms list: a bold title over a column of small detail lines
const HistoryContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  width: 100%;
  min-width: 0;
`;

const HistorySubtitle = styled.small`
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  color: #666;

  > * {
    word-wrap: break-word;
  }
`;

export default InventoryItemModal;
